'use client';

import { useEffect, useId, useState, type ReactNode } from 'react';
import { useAnchored } from './use-anchored';
import styles from './help-tip.module.css';
import { useT } from '@/lib/locale-context';

/**
 * HelpTip - the pinnable (?) explainer (docs/FLOW_DEFINITION.md §10.2: prose
 * lives only behind a (?) circle - hover opens, CLICK PINS). The kit's
 * `Tooltip` is deliberately hover-only: it dismisses on pointer-down and never
 * opens on touch, so it cannot carry an explanation someone must be able to
 * study or reach on a phone. This primitive is the counterpart for AUTHORED
 * explanations: hovering or keyboard-focusing the circle previews the panel,
 * clicking (the only path on touch) pins it open until Escape, an outside
 * press, or a second click on the circle. The pinned state is visible on the
 * circle itself (accent + `aria-pressed`), so "why is this panel staying"
 * always has an answer.
 *
 * Deliberately NOT a Menu/Sheet case (ui-kit rule "every dropdown/popover
 * opens as a Sheet on compact"): the panel is a short, non-interactive
 * sentence, tooltip-sized and viewport-clamped by `useAnchored`'s flip/shift,
 * so it never hangs off-screen at 375px the way action popovers did.
 */
export function HelpTip({
  children,
  label,
  className,
}: {
  /** The explanation - the one place a sentence is allowed (§10.2). */
  children: ReactNode;
  /** Accessible name for the (?) button. */
  label?: string;
  className?: string;
}) {
  const t = useT();
  const id = useId();
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const open = pinned || hovered;
  const { anchorRef, floatingRef } = useAnchored<HTMLButtonElement, HTMLDivElement>(open, {
    gap: 6,
  });

  // A pin survives until an explicit dismissal: Escape, or a press outside the
  // circle and its panel (the settled mockup's document-click unpin).
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || floatingRef.current?.contains(t)) return;
      setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPinned(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned, anchorRef, floatingRef]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={className ? `${styles.help} ${className}` : styles.help}
        aria-label={label ?? t('kit.helpTip.label')}
        aria-pressed={pinned}
        aria-describedby={open ? id : undefined}
        data-pinned={pinned || undefined}
        data-testid="help-tip"
        onClick={(e) => {
          // Never bubble into a draggable/collapsible host: opening help must
          // not also activate whatever the circle sits on.
          e.stopPropagation();
          setPinned((v) => !v);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerEnter={(e) => {
          if (e.pointerType !== 'touch') setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
        onFocus={(e) => {
          if (e.target.matches(':focus-visible')) setHovered(true);
        }}
        onBlur={() => setHovered(false)}
      >
        ?
      </button>
      {open && (
        <div ref={floatingRef} id={id} role="tooltip" className={styles.panel}>
          {children}
        </div>
      )}
    </>
  );
}
